import { useState } from "react";
import { api } from "@/lib/api";
import { Calendar as CalendarIcon, Globe, MapPin, X } from "lucide-react";
import { toast } from "sonner";

// Default to 1 hour from now, rounded up to next 15 minutes.
function defaultStartLocal() {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
  // Format as datetime-local string in user's timezone
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function WatchPartyDialog({ open, onClose, movie, onCreated }) {
  const [startsAtLocal, setStartsAtLocal] = useState(defaultStartLocal());
  const [mode, setMode] = useState("online");
  const [locationText, setLocationText] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open || !movie) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!startsAtLocal) { toast.error("Pick a date and time"); return; }
    const startsAtISO = new Date(startsAtLocal).toISOString();
    if (new Date(startsAtISO).getTime() < Date.now() - 60 * 1000) {
      toast.error("Choose a time in the future");
      return;
    }
    const location = mode === "online" ? "Online" : (locationText.trim() || "In person");
    setBusy(true);
    try {
      const { data } = await api.post(`/cinema/movies/${movie.id}/watch-party`, {
        starts_at: startsAtISO,
        location,
        note,
      });
      toast.success("Watch party scheduled — shared on the Events board");
      onCreated?.(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not schedule");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-jjblue/60 backdrop-blur-sm" data-testid="watch-party-dialog">
      <div className="jj-card w-full max-w-lg p-7 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--jj-muted)] hover:text-burgundy" data-testid="watch-party-close" aria-label="Close">
          <X className="h-5 w-5" />
        </button>
        <div className="overline text-burgundy flex items-center gap-1.5">
          <CalendarIcon className="h-3.5 w-3.5" /> Watch Party
        </div>
        <h2 className="font-serif text-3xl text-jjblue mt-2 leading-tight">Host a watch party</h2>
        <p className="mt-2 text-sm text-[var(--jj-muted)]">
          You&rsquo;re scheduling a community viewing of <span className="text-jjblue font-medium">&ldquo;{movie.title}&rdquo;</span>. The community will see it on the Events board with the title and a link back here.
        </p>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--jj-muted)]">When</label>
            <input
              type="datetime-local"
              className="input-jj mt-1"
              value={startsAtLocal}
              onChange={(e) => setStartsAtLocal(e.target.value)}
              data-testid="watch-party-when"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--jj-muted)]">Where</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode("online")}
                className={`flex items-center justify-center gap-2 py-2 rounded-full border text-sm transition-all ${
                  mode === "online" ? "bg-burgundy text-cream border-burgundy" : "bg-cream-surface text-jjblue border-jj hover:border-gold"
                }`}
                data-testid="watch-party-online"
              >
                <Globe className="h-4 w-4" /> Online
              </button>
              <button
                type="button"
                onClick={() => setMode("in_person")}
                className={`flex items-center justify-center gap-2 py-2 rounded-full border text-sm transition-all ${
                  mode === "in_person" ? "bg-burgundy text-cream border-burgundy" : "bg-cream-surface text-jjblue border-jj hover:border-gold"
                }`}
                data-testid="watch-party-inperson"
              >
                <MapPin className="h-4 w-4" /> In person
              </button>
            </div>
            {mode === "in_person" && (
              <input
                className="input-jj mt-2"
                placeholder="Address or venue name"
                value={locationText}
                onChange={(e) => setLocationText(e.target.value)}
                data-testid="watch-party-location"
              />
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--jj-muted)]">Note for attendees (optional)</label>
            <textarea
              rows={3}
              className="input-jj mt-1"
              placeholder="Bring popcorn. We&rsquo;ll pray before and discuss after."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="watch-party-note"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary !py-2 !px-4 text-sm">Cancel</button>
            <button disabled={busy} className="btn-primary !py-2 !px-5 text-sm" data-testid="watch-party-submit">
              {busy ? "Scheduling…" : "Schedule party"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
